<template>
  <div class="login-page">
    <div class="container-fluid p-0">
      <div class="row g-0 vh-100">
        <!-- Left Column with 3D shape and tagline -->
        <div class="col-12 col-md-6 d-flex flex-column justify-content-center left-column">
          <div class="left-content">
            <div class="logo-container">
              <img src="@/assets/blob.png" alt="3D Shape" class="fluid-shape" />
            </div>
            <h1 class="tagline">
              Optimizing Metering<br />
              Communication<br />
              with Predictive<br />
              Insights
            </h1>
          </div>
        </div>

        <!-- Right Column with login form -->
        <div class="col-12 col-md-6 d-flex align-items-center justify-content-center right-column">
          <div class="login-form-container">
            <div class="login-header">
              <p class="login-to">LOGIN TO</p>
              <h2 class="your-account">YOUR ACCOUNT</h2>
            </div>

            <!-- Error message display -->
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>

            <form @submit.prevent="requiresMFA ? handleMFA() : handleLogin()">
              <!-- Username -->
              <div class="form-group" v-if="!requiresMFA">
                <label for="username">User name :</label>
                <input
                    type="text"
                    id="username"
                    v-model="username"
                    required
                />
              </div>

              <!-- Password -->
              <div class="form-group" v-if="!requiresMFA">
                <label for="password">Password :</label>
                <input
                    type="password"
                    id="password"
                    v-model="password"
                    required
                />
              </div>

              <!-- MFA Code -->
              <div class="form-group" v-if="requiresMFA">
                <label for="mfa">MFA Code :</label>
                <input
                    type="text"
                    id="mfa"
                    v-model="mfaCode"
                    required
                />
              </div>

              <!-- Remember me -->
              <div class="remember-me" v-if="!requiresMFA">
                <label class="checkbox-container">
                  <input type="checkbox" v-model="rememberMe" />
                  <span class="checkmark"></span>
                  Remember me
                </label>
              </div>

              <!-- Submit Button -->
              <div class="login-button-container">
                <button type="submit" class="login-btn">
                  {{ requiresMFA ? 'Verify MFA' : 'Login' }}
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LoginPage',
  data() {
    return {
      username: '',
      password: '',
      mfaCode: '',
      rememberMe: false,
      requiresMFA: false,
      tempMFAToken: '',
      accessToken: '',
      refreshToken: '',
      errorMessage: '',
      isLoading: false
    };
  },
  created() {
    // If already authenticated, go straight to devices
    const token = localStorage.getItem('access_token');
    if (token) {
      this.$router.push('/Devices');
    }
  },
  methods: {
    async handleLogin() {
      this.errorMessage = '';
      this.isLoading = true;

      // Determine API base URL from env or fallback
      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';

      const payload = {
        email: this.username,
        password: this.password
      };

      try {
        const response = await axios.post(
            `${apiBaseUrl}/auth/login`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
        );

        if (response.data.requires_mfa) {
          this.requiresMFA = true;
          this.tempMFAToken = response.data.mfa_token;
          this.errorMessage = 'MFA is required. Please enter your MFA code.';
          return;
        }

        this.storeUserData(response.data);
        this.$router.push('/Devices');
      } catch (error) {
        console.error('Login failed:', error.response ? error.response.data : error.message);
        this.errorMessage =
            error.response?.data?.message ||
            'Login failed. Please check your credentials and try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async handleMFA() {
      this.errorMessage = '';
      this.isLoading = true;

      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';

      try {
        const response = await axios.post(
            `${apiBaseUrl}/auth/login/mfa`,
            { code: this.mfaCode },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.tempMFAToken}`
              }
            }
        );

        this.storeUserData(response.data);
        this.$router.push('/Devices');
      } catch (error) {
        console.error('MFA verification failed:', error.response ? error.response.data : error.message);
        this.errorMessage =
            error.response?.data?.message ||
            'MFA verification failed. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    storeUserData(data) {
      // Normalize token fields
      this.accessToken  = data.access_token  || data.token;
      this.refreshToken = data.refresh_token || data.refreshToken;

      // Persist tokens and user info
      localStorage.setItem('access_token', this.accessToken);
      localStorage.setItem('refresh_token', this.refreshToken);

      if (data.user) {
        localStorage.setItem('user_info', JSON.stringify(data.user));
      }
      if (data.role) {
        localStorage.setItem('user_role', data.role);
      }
      if (data.tenant_id) {
        localStorage.setItem('tenant_id', data.tenant_id);
      }
      if (data.accessible_tenants || data.accessibleTenants) {
        localStorage.setItem(
            'accessible_tenants',
            JSON.stringify(data.accessible_tenants || data.accessibleTenants)
        );
      }
      if (data.permissions) {
        localStorage.setItem('permissions', JSON.stringify(data.permissions));
      }
      if (data.expires_at || data.expiresAt) {
        localStorage.setItem(
            'token_expires_at',
            (data.expires_at || data.expiresAt).toString()
        );
      }
      if (this.rememberMe) {
        localStorage.setItem('remember_user', 'true');
      }
    },

    clearForm() {
      this.username = '';
      this.password = '';
      this.mfaCode = '';
      this.errorMessage = '';
    }
  }
};
</script>

<style scoped>
.login-page {
  background: linear-gradient(to bottom, #111111 0%, #1A1A1A 100%);
  min-height: 100vh;
  width: 100%;
  color: white;
  font-family: 'Inter', sans-serif;
}

.left-column {
  min-height: 60vh;
}

.right-column {
  background-color: rgba(86, 86, 86, 0.1);
  min-height: 40vh;
}

.error-message {
  margin-bottom: 16px;
  padding: 10px;
  background-color: rgba(255, 0, 0, 0.2);
  color: #ffdddd;
  text-align: center;
  border-radius: 4px;
  font-size: 14px;
}

@media (max-width: 767.98px) {
  .left-column {
    min-height: 60vh;
    order: 1;
  }

  .right-column {
    min-height: 40vh;
    order: 2;
  }

  .tagline {
    font-size: 36px !important;
  }

  .fluid-shape {
    width: 350px;
  }

  .left-content {
    padding-left: 10% !important;
    padding-right: 10% !important;
  }
}

.left-content {
  padding-left: 15%;
  padding-right: 5%;
}

.logo-container {
  margin-bottom: 40px;
}

.fluid-shape {
  max-width: 350px;
}

.tagline {
  font-size: 64px;
  font-weight: 400;
  line-height: 145%;
  color: #ffffff;
  margin-top: 30px;
}

.login-form-container {
  width: 100%;
  max-width: 450px;
}

.login-header {
  margin-bottom: 50px;
}

.login-to {
  font-size: 13px;
  font-weight: 700;
  line-height: 120%;
  margin-bottom: 8px;
  color: #ffffff;
  letter-spacing: 3px;
}

.your-account {
  font-size: 32px;
  font-weight: 400;
  margin-top: 15px;
  color: #ffffff;
  letter-spacing: 3px;
}

.form-group {
  margin-bottom: 24px;
}

label {
  display: block;
  font-size: 11px;
  font-weight: 400;
  margin-bottom: 10px;
  color: #ffffff;
}

input {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background-color: transparent;
  color: white;
  font-size: 14px;
}

input:focus {
  outline: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.6);
}

.remember-me {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  font-size: 11px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  height: 16px;
  width: 16px;
  background-color: transparent;
  border: 1px solid #ffffff;
  margin-right: 8px;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #ffffff;
}

.login-button-container {
  text-align: right;
}

.login-btn {
  padding: 10px 40px;
  background-color: transparent;
  color: white;
  border: 1px solid #ffffff;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.login-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>
